import { useMemo } from 'react';

import { GlobalAccount } from '@ndvr/auth-api';
import { TaskStatus, TaskType, TaskView } from '@ndvr/customer-api';

import { stripHtmlToText } from 'utils';

interface UseFilteredTaskViewsProps {
    tasks: TaskView[];
    selectedAssignees?: string[];
    selectedHouseholds?: string[];
    selectedTaskTypes?: TaskType[];
    selectedTaskStatuses?: TaskStatus[];
    searchTerm?: string;
    householdMap?: Map<string, GlobalAccount>;
}

export const useFilteredTaskViews = ({
    tasks,
    selectedAssignees = [],
    selectedHouseholds = [],
    selectedTaskTypes = [],
    selectedTaskStatuses = [],
    searchTerm,
    householdMap,
}: UseFilteredTaskViewsProps) => {
    return useMemo(() => {
        if (!tasks.length) return [];

        let filteredTasks = [...tasks];

        // Apply search filtering
        const search = searchTerm?.toLowerCase().trim();
        if (search) {
            filteredTasks = filteredTasks.filter((task) => {
                const householdName = task.householdGUID
                    ? householdMap?.get(task.householdGUID)?.globalAccountNamePlain
                    : undefined;
                return (
                    task.title?.toLowerCase().includes(search) ||
                    stripHtmlToText(task.description || '')
                        .toLowerCase()
                        .includes(search) ||
                    householdName?.toLowerCase().includes(search)
                );
            });
        }

        // Apply assignee filtering
        if (selectedAssignees.length > 0) {
            filteredTasks = filteredTasks.filter((task) => selectedAssignees.includes(task.assignee));
        }

        // Apply household filtering
        if (selectedHouseholds.length > 0) {
            filteredTasks = filteredTasks.filter(
                (task) => task.householdGUID != null && selectedHouseholds.includes(task.householdGUID),
            );
        }

        // Apply task type filtering
        if (selectedTaskTypes.length > 0) {
            filteredTasks = filteredTasks.filter((task) => selectedTaskTypes.includes(task.type));
        }

        // Apply task status filtering
        if (selectedTaskStatuses.length > 0) {
            filteredTasks = filteredTasks.filter((task) => selectedTaskStatuses.includes(task.status));
        }

        return filteredTasks;
    }, [
        tasks,
        selectedAssignees,
        selectedHouseholds,
        selectedTaskTypes,
        selectedTaskStatuses,
        searchTerm,
        householdMap,
    ]);
};
